import { useCallback } from "react";
import { getHost } from "./getHost";

// Asks the host (presenter) to open its "Request additional licenses"
// modal — the form that submits a Slack notification to the CS team via
// the presenter's pricing API. We use the host modal instead of
// rendering our own because:
//   - the form already exists on the presenter side
//     (`RequestAdditionalLicenseModal.vue`)
//   - the request goes to a presenter-owned pricing API
//     (`hookBulkPricingAction`) that's not exposed to this embed
//   - keeping it on the host means CS-team copy and behaviour stay in
//     one place across the My Plan and My Team flows
// See AHA-42841 for the bug this fixes (CS-managed teams were being
// sent to the Stripe self-serve checkout).
//
// The returned function is always safe to call. When running outside
// the host (standalone dev) or against an older host that doesn't pass
// the prop, it's a no-op — `canRequest` exposes that case so callers
// can route to a fallback (e.g. a "contact support" toast) instead of
// silently doing nothing.
export interface HostRequestAdditionalLicense {
  request: () => void;
  /** True when the host exposes the bridge — check before relying on
   *  `request` for the only path forward. Snapshot at hook-call time;
   *  fine for our use because the zoid handshake completes before any
   *  user interaction. */
  canRequest: boolean;
}

export function useHostRequestAdditionalLicense(): HostRequestAdditionalLicense {
  const request = useCallback(() => {
    // Re-read at call time — zoid populates xprops asynchronously, so
    // the host may not have been available when the hook first ran.
    const host = getHost();
    host?.openRequestAdditionalLicenseModal?.();
  }, []);
  const canRequest = Boolean(getHost()?.openRequestAdditionalLicenseModal);
  return { request, canRequest };
}
